import { api } from '@/shared/api/client'

/**
 * Opposition Command — `/api/v1/opposition`.
 *
 * ## Two shapes here encode rules the UI must not soften
 *
 * **Scores are nullable.** `threatScore: null` means "not scored — no publishable polling
 * estimate", and it is NOT zero. A client that coalesced it to 0 would rank an unmeasured
 * candidate below every measured one, which is precisely backwards.
 *
 * **`scoresAsOf` travels with every score.** The columns are a cache of an
 * `intel_assessment`; a cached number whose age the reader cannot see is a lie with a
 * timestamp, and this matrix is read in the last fortnight of a campaign.
 *
 * There is no field anywhere in this module for suppression or demobilisation. The lawful
 * equivalent is `expectedSwingPp` on a play, measured TOWARD us.
 */

export interface Target {
  id: string
  electionId: string
  candidacyId: string | null
  subjectId: string
  targetTypeCode: string
  sideCode: string
  displayName: string
  seatType: string | null
  unitId: string | null
  unitPath: string | null
  clearanceCode: string
  killChainStageCode: string
  priority: number
  /** Null = not scored. Never render as zero. */
  threatScore: number | null
  electabilityScore: number | null
  vulnerabilityScore: number | null
  momentumCode: string | null
  signalScore: number | null
  scoresAsOf: string | null
  warChestKes: number | null
  statusCode: string
  openedAt: string
  closedAt: string | null
  closureNote: string | null
  version: number
}

export interface MatrixPoint {
  targetId: string
  name: string
  electability: number | null
  vulnerability: number | null
  threat: number | null
  momentumCode: string | null
  stageCode: string
  scoresAsOf: string | null
}

export interface Surface {
  id: string
  targetId: string
  categoryCode: string
  surface: string
  detail: string | null
  severity: number
  defensibility: number
  publicExposure: number
  /** DERIVED from linked evidence. There is no input for it, by design. */
  evidenceStrength: number
  recommendation: string | null
  rationale: string
  statusCode: string
  firstSeenAt: string
  lastReviewedAt: string | null
  evidence: { id: string; itemId: string; roleCode: string; note: string | null }[]
}

/**
 * A working file on a surface.
 *
 * NOT evidence, and there is no field here that could make it so. These support the WRITE-UP
 * of a claim; `evidenceStrength` moves only when a graded `intel_item` is linked, because a
 * dragged-in file has no source and no Admiralty pair. Append-only, like every other
 * attachment in this platform: a bad one is marked, never deleted.
 */
export interface SurfaceFile {
  id: string
  surfaceId: string
  storageFileId: string
  filename: string
  contentType: string | null
  sizeBytes: number | null
  caption: string | null
  dependable: boolean
  undependableReason: string | null
  attachedBy: string | null
  attachedAt: string
}

/**
 * One ask of the advisory council about a target, at one kill-chain stage.
 *
 * The run fields are null until it finishes. QUEUED is a real state and the screen says so
 * rather than rendering an empty report — a model answer that has not arrived is not the
 * same as one that came back empty.
 */
export interface Advice {
  id: string
  targetId: string
  stageCode: string
  playbookCode: string
  question: string | null
  advisorRunId: string
  requestedBy: string | null
  requestedAt: string
  runStatusCode: string
  reportId: string | null
  headline: string | null
  executiveSummary: string | null
  confidenceCode: string | null
  /** Which model wrote it. On the row, because "the AI said" is not an attribution. */
  generatedByModel: string | null
  completedAt: string | null
}

export interface Play {
  id: string
  targetId: string
  surfaceId: string | null
  playTypeCode: string
  title: string
  tactic: string
  executionPlan: string | null
  audienceSegment: string | null
  channelsJson: string | null
  /** Percentage points TOWARD us. There is no opposite field. */
  expectedSwingPp: number | null
  riskLevelCode: string
  budgetKes: number | null
  timelineLabel: string | null
  legalReviewStatusCode: string
  legalReviewedBy: string | null
  legalReviewedAt: string | null
  legalConditions: string | null
  legalNote: string | null
  statusCode: string
  createdBy: string | null
  version: number
}

export interface Operation {
  id: string
  ref: string
  electionId: string
  name: string
  targetId: string | null
  objective: string
  stageCode: string
  /** A PERSON, never a team. The DEPLOY gate refuses without one. */
  accountableUserId: string
  teamLabel: string | null
  budgetKes: number | null
  spentKes: number | null
  riskLevelCode: string
  startsOn: string | null
  targetCompletionOn: string | null
  statusCode: string
  outcomeCode: string | null
  outcomeNote: string | null
  version: number
}

export interface OperationEvent {
  id: string
  eventTypeCode: string
  detail: string | null
  payloadJson: string | null
  occurredAt: string
  actorId: string | null
}

export interface OperationDetail {
  operation: Operation
  log: OperationEvent[]
  playIds: string[]
}

/** A stage move with the evidence that justified it — the defensible half of the chain. */
export interface StageTransition {
  id: string
  fromStageCode: string
  toStageCode: string
  gateSatisfiedJson: string
  movedBy: string
  movedAt: string
  note: string | null
}

export interface WatchRow {
  target: Target
  reason: string
  signalScore: number
  /** Seven days of signal, so a spike and a trend can be told apart. */
  sparkline: number[]
}

export interface KillChainColumn {
  stageCode: string
  label: string
  targets: Target[]
}

export interface Dashboard {
  targetCount: number
  activeOperations: number
  surfacesEvidenced: number
  playsAwaitingReview: number
  /** Not padding: a matrix that dropped these would read as the complete field. */
  unscoredTargets: number
  matrix: MatrixPoint[]
  topThreats: Target[]
  watchlist: WatchRow[]
  generatedAt: string
}

export interface ScandalRow {
  targetId: string
  targetName: string
  surfaceId: string
  categoryCode: string
  surface: string
  severity: number
  evidenceStrength: number
  statusCode: string
}

export interface LiveFeedRow {
  targetId: string
  name: string
  sideCode: string
  seatType: string | null
  /** A PROPORTION in [0,1], or null when there is no publishable estimate. Never 0 for absent. */
  share: number | null
  moe95: number | null
  baseN: number | null
  waveLabel: string | null
  computedAt: string | null
  momentumCode: string | null
  /** Why there is no share. Rendered — the absence is the finding. */
  noEstimateReason: string | null
}

export interface LiveFeed {
  rows: LiveFeedRow[]
  ours: LiveFeedRow | null
  topOpponent: LiveFeedRow | null
  leadPp: number | null
  /** True when the lead is inside the combined margin — "too close to call", not "ahead". */
  withinMargin: boolean | null
  unmeasured: number
  generatedAt: string
}

const base = '/opposition'

export const getDashboard = (electionId?: string) =>
  api.get<Dashboard>(`${base}/dashboard${electionId ? `?electionId=${electionId}` : ''}`)

export const getMatrix = (electionId?: string) =>
  api.get<MatrixPoint[]>(`${base}/matrix${electionId ? `?electionId=${electionId}` : ''}`)

export const getKillChain = (electionId?: string) =>
  api.get<KillChainColumn[]>(`${base}/killchain${electionId ? `?electionId=${electionId}` : ''}`)

export const getWatchlist = () => api.get<WatchRow[]>(`${base}/watchlist`)

export const listTargets = (electionId?: string) =>
  api.get<Target[]>(`${base}/targets${electionId ? `?electionId=${electionId}` : ''}`)

export const getTarget = (id: string) => api.get<Target>(`${base}/targets/${id}`)

export const openTarget = (body: unknown) => api.post<Target>(`${base}/targets`, body)

export const moveStage = (id: string, stageCode: string, note?: string) =>
  api.post<Target>(`${base}/targets/${id}/stage`, { stageCode, note })

export const stageHistory = (id: string) =>
  api.get<StageTransition[]>(`${base}/targets/${id}/stage-history`)

export const rescore = (id: string) => api.post<Target>(`${base}/targets/${id}/rescore`)

export const watchTarget = (id: string, reason: string) =>
  api.post<Target>(`${base}/targets/${id}/watch`, { reason })

export const unwatchTarget = (id: string, reason?: string) =>
  api.del<Target>(`${base}/targets/${id}/watch${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`)

export const listSurfaces = (targetId: string) =>
  api.get<Surface[]>(`${base}/targets/${targetId}/surfaces`)

export const recordSurface = (targetId: string, body: unknown) =>
  api.post<Surface>(`${base}/targets/${targetId}/surfaces`, body)

export const linkEvidence = (surfaceId: string, body: unknown) =>
  api.post<Surface>(`${base}/surfaces/${surfaceId}/evidence`, body)

export const listSurfaceFiles = (surfaceId: string) =>
  api.get<SurfaceFile[]>(`${base}/surfaces/${surfaceId}/files`)

/** Multipart, so hand-rolled — the shared client stamps application/json on every body. */
export const attachSurfaceFiles = async (
  surfaceId: string, files: File[], caption?: string,
): Promise<SurfaceFile[]> => {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  if (caption?.trim()) form.append('caption', caption.trim())
  const { apiBase } = await import('@/config/runtime-config')
  const { getAccessToken } = await import('@/shared/auth/tokenStore')
  const res = await fetch(`${apiBase}/opposition/surfaces/${surfaceId}/files`, {
    method: 'POST',
    body: form,
    headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    credentials: 'include',
  })
  if (!res.ok) throw await res.json()
  return res.json() as Promise<SurfaceFile[]>
}

/** What exists INSTEAD of a delete. Unmarking needs a reason of at least 20 characters. */
export const markSurfaceFile = (attachmentId: string, dependable: boolean, reason?: string) =>
  api.post<SurfaceFile>(`${base}/surface-files/${attachmentId}/dependable`,
    { dependable, reason })

export const listAdvice = (targetId: string) =>
  api.get<Advice[]>(`${base}/targets/${targetId}/advice`)

/**
 * Ask the council. Available at ANY kill-chain stage.
 *
 * Note what is NOT in this body: a prompt. The playbook is CHOSEN from five reviewed
 * recipes — the server refuses anything else — and the stage is read from the target rather
 * than sent, so what gets recorded is where it actually was. A client that could send a
 * prompt would be an ungoverned model call against a named person.
 */
export const askAdvice = (targetId: string, body: {
  playbookCode?: string; question?: string
}) => api.post<Advice>(`${base}/targets/${targetId}/advice`, body)

export const listScandals = (electionId?: string) =>
  api.get<ScandalRow[]>(`${base}/scandals${electionId ? `?electionId=${electionId}` : ''}`)

export const listPlays = (targetId: string) =>
  api.get<Play[]>(`${base}/targets/${targetId}/plays`)

export const draftPlay = (targetId: string, body: unknown) =>
  api.post<Play>(`${base}/targets/${targetId}/plays`, body)

/**
 * Refused when the reviewer wrote the play — even holding both authorities. A named waiver
 * (`opposition:review-own`, which SUPER_ADMIN holds through the wildcard) lifts it, and every
 * waived review is stamped `selfReviewed` in the audit trail.
 */
export const legalReview = (playId: string, body: unknown) =>
  api.post<Play>(`${base}/plays/${playId}/legal-review`, body)

export const setPlayStatus = (playId: string, statusCode: string) =>
  api.post<Play>(`${base}/plays/${playId}/status`, { statusCode })

export const listOperations = (electionId: string) =>
  api.get<Operation[]>(`${base}/operations?electionId=${electionId}`)

export const getOperation = (id: string) =>
  api.get<OperationDetail>(`${base}/operations/${id}`)

export const openOperation = (body: unknown) => api.post<Operation>(`${base}/operations`, body)

export const recordEvent = (id: string, body: unknown) =>
  api.post<OperationEvent>(`${base}/operations/${id}/events`, body)

export const attachPlayToOperation = (id: string, playId: string) =>
  api.post<Operation>(`${base}/operations/${id}/plays`, { playId })

export const moveOperationStage = (id: string, stageCode: string, note?: string) =>
  api.post<Operation>(`${base}/operations/${id}/stage`, { stageCode, note })

/**
 * Closing is what NEUTRALISES the target: the service moves it and writes a stage transition
 * carrying the outcome. `BACKFIRED` is a first-class outcome — a vocabulary with no way to
 * say "this hurt us" produces a campaign history in which nothing ever went wrong.
 */
export const closeOperation = (id: string, body: unknown) =>
  api.post<Operation>(`${base}/operations/${id}/close`, body)

export const getLiveFeed = (electionId?: string) =>
  api.get<LiveFeed>(`${base}/live-feed${electionId ? `?electionId=${electionId}` : ''}`)

export const getVocabulary = () =>
  api.get<Record<string, { code: string; label: string }[]>>(`${base}/vocabulary`)
